import React, { useState, useEffect } from "react";
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  FormHelperText,
  Tooltip
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { useApiFetch } from "../api/apiClient";
import { fmtDateTime, fmtDate } from "../utils/dateFormat";

// Generate tax roll year options (current year + 10 years ahead)
const generateTaxRollYearOptions = () => {
  const currentYear = new Date().getFullYear();
  const options = [];
  for (let i = 0; i <= 10; i++) {
    const year = currentYear + i;
    const nextYear = year + 1;
    const lastTwoDigits = String(nextYear).slice(-2);
    options.push(`${year}-${lastTwoDigits}`);
  }
  return options;
};

export default function SubmissionPeriodConfig() {
  const [periods, setPeriods] = useState([]);
  const [activePeriod, setActivePeriod] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingPeriod, setEditingPeriod] = useState(null);
  const [formData, setFormData] = useState({
    taxRollYear: "",
    submissionPeriodStart: "",
    submissionDeadline: ""
  });
  const [validationError, setValidationError] = useState("");

  const apiFetch = useApiFetch();

  useEffect(() => {
    loadData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      const [periodsRes, activeRes] = await Promise.all([
        apiFetch("/submission-period"),
        apiFetch("/submission-period/active").catch(() => null)
      ]);

      if (!periodsRes.ok) throw new Error(`HTTP ${periodsRes.status}`);
      const periodsData = await periodsRes.json();
      const activeData = activeRes && activeRes.ok ? await activeRes.json() : null;

      setPeriods(Array.isArray(periodsData) ? periodsData : []);
      setActivePeriod(activeData);
    } catch (err) {
      setError(err.message || "Failed to load submission periods");
    } finally {
      setLoading(false);
    }
  };

  // Get available tax roll years (not yet created)
  const getAvailableYears = () => {
    const allYears = generateTaxRollYearOptions();
    const existingYears = periods.map((p) => p.taxRollYear);
    return allYears.filter((year) => !existingYears.includes(year));
  };

  const handleOpenCreateDialog = () => {
    setFormData({
      taxRollYear: "",
      submissionPeriodStart: "",
      submissionDeadline: ""
    });
    setValidationError("");
    setCreateDialogOpen(true);
  };

  const handleOpenEditDialog = (period) => {
    setEditingPeriod(period);
    setFormData({
      taxRollYear: period.taxRollYear,
      submissionPeriodStart: period.submissionPeriodStart,
      submissionDeadline: period.submissionDeadline
    });
    setEditDialogOpen(true);
  };

  const handleCloseDialogs = () => {
    setCreateDialogOpen(false);
    setEditDialogOpen(false);
    setEditingPeriod(null);
    setFormData({
      taxRollYear: "",
      submissionPeriodStart: "",
      submissionDeadline: ""
    });
    setValidationError("");
  };

  const handleCreate = async () => {
    // Validation: ensure year is selected
    if (!formData.taxRollYear) {
      setValidationError("Please select a Tax Roll Year.");
      return;
    }

    setValidationError("");
    try {
      const res = await apiFetch("/submission-period", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error || `HTTP ${res.status}`);
      }
      handleCloseDialogs();
      loadData();
    } catch (err) {
      setValidationError(err.message || "Failed to create submission period");
    }
  };

  const handleUpdate = async () => {
    try {
      const res = await apiFetch(`/submission-period/${editingPeriod.id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(formData)
      });
      if (!res.ok) {
        const error = await res.text();
        throw new Error(error || `HTTP ${res.status}`);
      }
      handleCloseDialogs();
      loadData();
    } catch (err) {
      alert(err.message || "Failed to update submission period");
    }
  };

  const getStatusChip = (period) => {
    if (period.isArchived) {
      return <Chip label="Closed" color="error" size="small" />;
    }
    if (period.isActive) {
      return <Chip label="Active" color="success" size="small" />;
    }
    return <Chip label="Inactive" color="default" size="small" />;
  };

  if (loading) {
    return (
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  const availableYears = getAvailableYears();

  return (
    <Box sx={{ maxWidth: 1400, p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Submission Period Configuration for the Online Preliminary Notification Form
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {/* Active Period Card */}
      {activePeriod && (
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Current Active Period
            </Typography>
            <Box sx={{ display: "grid", gridTemplateColumns: "repeat(2, 1fr)", gap: 2 }}>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Tax Roll Year
                </Typography>
                <Typography variant="body1">{activePeriod.taxRollYear}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Status
                </Typography>
                <Box sx={{ mt: 0.5 }}>{getStatusChip(activePeriod)}</Box>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Submission Period Start
                </Typography>
                <Typography variant="body1">{fmtDate(activePeriod.submissionPeriodStart)}</Typography>
              </Box>
              <Box>
                <Typography variant="body2" color="text.secondary">
                  Submission Deadline
                </Typography>
                <Typography variant="body1">{fmtDate(activePeriod.submissionDeadline)}</Typography>
              </Box>
              {activePeriod.submissionPeriodEnd && (
                <Box>
                  <Typography variant="body2" color="text.secondary">
                    Closed On
                  </Typography>
                  <Typography variant="body1">{fmtDate(activePeriod.submissionPeriodEnd)}</Typography>
                </Box>
              )}
            </Box>
          </CardContent>
        </Card>
      )}


      {/* Create Button */}
      <Box sx={{ mb: 2 }}>
        <Tooltip
          title={
            activePeriod && !activePeriod.isArchived
              ? "Please archive the current submission period before creating a new one"
              : ""
          }
          arrow
        >
          <span>
            <Button
              variant="contained"
              onClick={handleOpenCreateDialog}
              disabled={activePeriod && !activePeriod.isArchived}
            >
              Create New Submission Period
            </Button>
          </span>
        </Tooltip>
      </Box>

      {/* Periods Table */}
      <Typography variant="h6" gutterBottom>
        All Submission Periods
      </Typography>
      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Tax Roll Year</TableCell>
              <TableCell>Status</TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Period Start
                  <Tooltip
                    title="This date controls when the online preliminary notification form will be available for the applicable tax year."
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Deadline
                  <Tooltip
                    title="This date is the submission deadline communicated in the email that provides the agency with access to the Online Preliminary Notification Form."
                    arrow
                  >
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  Period End
                  <Tooltip title="This date is populated when the Archive Process is executed." arrow>
                    <InfoOutlinedIcon sx={{ fontSize: 16, color: "text.secondary", cursor: "help" }} />
                  </Tooltip>
                </Box>
              </TableCell>
              <TableCell>Created By</TableCell>
              <TableCell>Created At</TableCell>
              <TableCell>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {Array.isArray(periods) && periods.length > 0 ? (
              periods.map((period) => (
                <TableRow key={period.id}>
                  <TableCell>{period.taxRollYear}</TableCell>
                  <TableCell>{getStatusChip(period)}</TableCell>
                  <TableCell>{fmtDate(period.submissionPeriodStart)}</TableCell>
                  <TableCell>{fmtDate(period.submissionDeadline)}</TableCell>
                  <TableCell>
                    {period.submissionPeriodEnd ? fmtDate(period.submissionPeriodEnd) : "—"}
                  </TableCell>
                  <TableCell>{period.createdBy}</TableCell>
                  <TableCell>{fmtDateTime(period.createdAt)}</TableCell>
                  <TableCell>
                    {!period.isArchived && (
                      <Button size="small" onClick={() => handleOpenEditDialog(period)}>
                        Edit
                      </Button>
                    )}
                  </TableCell>
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <Typography variant="body2" color="text.secondary">
                    No submission periods found
                  </Typography>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Create New Submission Period</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Creating a new submission period will deactivate any existing active period and set the new period
            as active.
          </DialogContentText>
          {validationError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {validationError}
            </Alert>
          )}
          {availableYears.length === 0 ? (
            <Alert severity="warning" sx={{ mb: 2 }}>
              All tax roll years up to 10 years ahead have been created. No new periods can be created at this
              time.
            </Alert>
          ) : (
            <>
              <FormControl fullWidth margin="dense" sx={{ mb: 2 }}>
                <InputLabel id="create-tax-roll-year-label">Tax Roll Year *</InputLabel>
                <Select
                  labelId="create-tax-roll-year-label"
                  label="Tax Roll Year *"
                  value={formData.taxRollYear}
                  onChange={(e) => setFormData({ ...formData, taxRollYear: e.target.value })}
                  autoFocus
                >
                  {availableYears.map((year) => (
                    <MenuItem key={year} value={year}>
                      {year}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              <TextField
                margin="dense"
                label="Submission Period Start"
                type="date"
                fullWidth
                required
                value={formData.submissionPeriodStart}
                onChange={(e) => setFormData({ ...formData, submissionPeriodStart: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="This date controls when the online preliminary notification form will be available for the applicable tax year."
                sx={{ mb: 2 }}
              />
              <TextField
                margin="dense"
                label="Submission Deadline"
                type="date"
                fullWidth
                required
                value={formData.submissionDeadline}
                onChange={(e) => setFormData({ ...formData, submissionDeadline: e.target.value })}
                InputLabelProps={{ shrink: true }}
                helperText="This date is the submission deadline communicated in the email that provides the agency with access to the Online Preliminary Notification Form."
              />
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button onClick={handleCreate} variant="contained" disabled={availableYears.length === 0}>
            Create
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={editDialogOpen} onClose={handleCloseDialogs} maxWidth="sm" fullWidth>
        <DialogTitle>Edit Submission Period</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Update the submission period details. Note: Tax Roll Year cannot be changed after creation.
            Archived periods cannot be edited.
          </DialogContentText>
          <TextField
            margin="dense"
            label="Tax Roll Year"
            type="text"
            fullWidth
            value={formData.taxRollYear}
            disabled
            sx={{ mb: 2 }}
            helperText="Tax Roll Year cannot be modified"
          />
          <TextField
            autoFocus
            margin="dense"
            label="Submission Period Start"
            type="date"
            fullWidth
            required
            value={formData.submissionPeriodStart}
            onChange={(e) => setFormData({ ...formData, submissionPeriodStart: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="This date controls when the online preliminary notification form will be available for the applicable tax year."
            sx={{ mb: 2 }}
          />
          <TextField
            margin="dense"
            label="Submission Deadline"
            type="date"
            fullWidth
            required
            value={formData.submissionDeadline}
            onChange={(e) => setFormData({ ...formData, submissionDeadline: e.target.value })}
            InputLabelProps={{ shrink: true }}
            helperText="This date is the submission deadline communicated in the email that provides the agency with access to the Online Preliminary Notification Form."
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialogs}>Cancel</Button>
          <Button onClick={handleUpdate} variant="contained">
            Update
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
